import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// ─────────────────────────────────────────────────────────────────────
// 🔒 SECURITY — Auth dual mode
//
// PRIMARIO: cookie httpOnly + Secure + SameSite=Lax (emitida por backend).
//   • Axios usa `withCredentials: true` → cookies se envían automáticamente.
//   • Inmune a XSS (no accesible desde JS).
//
// FALLBACK: Bearer token desde sessionStorage (no localStorage).
//   • Si las cookies se bloquean (browser strict mode, ITP, política
//     corporativa, modo incógnito agresivo), el frontend usa el token
//     que recibe del backend en el body del login.
//   • sessionStorage (en vez de localStorage) → se borra al cerrar la
//     pestaña, menor superficie de ataque.
//   • Se envía como header `Authorization: Bearer ${token}`.
//   • El backend acepta ambos (cookie primero, fallback Bearer).
//
// CSRF (double-submit cookie pattern):
//   • Backend setea cookie `csrf_token` NO-httpOnly + lo retorna en el body
//     del login.
//   • Frontend lee desde la cookie O del sessionStorage (fallback) y la
//     envía en header `X-CSRF-Token` en cada mutación.
//   • Backend valida header == cookie. Si no hay cookie csrf (caso Bearer
//     puro), el CSRF check se salta (no aplica sin cookies).
// ─────────────────────────────────────────────────────────────────────

const TOKEN_KEY = "procure_auth_token";  // sessionStorage (no persiste entre tabs cerradas)
const CSRF_KEY = "procure_csrf_token";

/** Lee una cookie por nombre desde document.cookie. */
function getCookie(name) {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
}

export function getAuthToken() {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setAuthToken(token) {
  try {
    if (token) sessionStorage.setItem(TOKEN_KEY, token);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    // ignore (Safari private mode)
  }
}

export function getCsrfToken() {
  // Prefer cookie (cuando funciona). Fallback a sessionStorage si la cookie
  // se bloqueó (browser strict mode).
  return getCookie("csrf_token") || sessionStorage.getItem(CSRF_KEY);
}

export function setCsrfToken(token) {
  try {
    if (token) sessionStorage.setItem(CSRF_KEY, token);
    else sessionStorage.removeItem(CSRF_KEY);
  } catch {
    // ignore
  }
}

/** Limpia residuos legacy y todos los tokens. */
export function clearAllTokens() {
  setAuthToken(null);
  setCsrfToken(null);
  try {
    localStorage.removeItem("procure_token");  // legacy
  } catch {
    // ignore
  }
}

// withCredentials=true → axios envía cookies automáticamente cuando funcionan.
export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

const MUTATING_METHODS = new Set(["post", "put", "patch", "delete"]);

api.interceptors.request.use((config) => {
  const url = config.url || "";
  const method = (config.method || "").toLowerCase();

  // Fallback Bearer: si las cookies se bloquearon, enviar token en header.
  // El backend prioriza la cookie httpOnly; este header solo se usa cuando
  // la cookie no llega.
  const token = getAuthToken();
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // CSRF en mutaciones (excepto endpoints auth públicos)
  if (MUTATING_METHODS.has(method)) {
    const isExempt = (
      url.startsWith("/auth/login") ||
      url.startsWith("/auth/register") ||
      url.startsWith("/auth/password-reset/")
    );
    if (!isExempt) {
      const csrf = getCsrfToken();
      if (csrf) {
        config.headers["X-CSRF-Token"] = csrf;
      }
    }
  }
  return config;
});

// Response interceptor: en 401 fuera de auth, limpiar tokens (sesión expiró).
// Además: si el backend rota el CSRF (header X-CSRF-Rotated: 1), re-cachear
// el nuevo token leyendo la cookie actualizada por el backend.
api.interceptors.response.use(
  (r) => {
    try {
      if (r?.headers?.["x-csrf-rotated"] === "1") {
        const fresh = getCookie("csrf_token");
        if (fresh) sessionStorage.setItem(CSRF_KEY, fresh);
      }
    } catch (_) { /* noop */ }
    return r;
  },
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || "";
    if (status === 401 && !url.startsWith("/auth/")) {
      // Limpieza defensiva de token stale
      clearAllTokens();
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Ocurrió un error. Intenta de nuevo.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export function formatCOP(value) {
  const n = Number(value || 0);
  return new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(n);
}

export function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("es-CO", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}
